import { useEffect, useState } from "react";
import { Activity } from "lucide-react";
import { AdminLayout } from "./layout/AdminLayout";
import { useAuthContext } from "@/contexts/AuthContext";
import { wsManager } from "@/lib/websocket";
import {
    AlertsPanel,
    ConnectionStatus,
    PerformanceCharts,
    QuickActions,
    RealTimeActivity,
    ServerStatusGrid,
    SystemMetricsCards,
} from "./components/dashboard";

type WsStatus = "connected" | "connecting" | "disconnected";

export default function AdminDashboard() {
    const { user, logout } = useAuthContext();
    const [wsStatus, setWsStatus] = useState<WsStatus>("connecting");

    useEffect(() => {
        // Connect on demand (also in dev) to keep the dashboard live when backend supports WS.
        wsManager.connect();

        const update = () => {
            setWsStatus(wsManager.isConnected() ? "connected" : "disconnected");
        };

        update();
        const statusInterval = window.setInterval(update, 2000);

        const onConnected = () => setWsStatus("connected");
        const onDisconnected = () => setWsStatus("disconnected");
        wsManager.on("connected", onConnected);
        wsManager.on("disconnected", onDisconnected);

        return () => {
            wsManager.off("connected", onConnected);
            wsManager.off("disconnected", onDisconnected);
            window.clearInterval(statusInterval);
        };
    }, []);

    if (!user) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <AdminLayout user={user} onLogout={() => void logout()}>
            <div className="space-y-6 sm:space-y-8">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-1">
                        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                            Panoramica
                        </h1>
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Activity className="h-4 w-4" />
                            Stato sistema e servizi in tempo reale
                        </p>
                    </div>
                    <ConnectionStatus status={wsStatus} />
                </div>

                <SystemMetricsCards />

                <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-1">
                        <QuickActions />
                    </div>
                    <div className="lg:col-span-2">
                        <AlertsPanel />
                    </div>
                </div>

                <PerformanceCharts />

                <ServerStatusGrid />

                <RealTimeActivity />
            </div>
        </AdminLayout>
    );
}
